/**
 * Squish Deli product page behaviour (templates/product.json: sd-pdp-* blocks and sections).
 *
 * 1. Variant sync: Horizon's variant picker already fetches the page HTML for the new variant and hands it to
 *    ProductSelectEvent listeners. Every [data-sd-sync="key"] region is morphed from that HTML, so the price tag,
 *    platter link (?variant=), ticket and sticky bar follow the chosen variant without a second request.
 * 2. Horizon's add-to-cart becomes the secondary action: its label is replaced with the
 *    data-sd-pdp-buy-label of the sd-pdp-cta block ("Buy just this one"; add-ons: "Add to bag"), and kept that
 *    way after Horizon morphs the button. Without JS the Horizon label stays.
 * 3. Mobile sticky bar ([data-sd-pdp-sticky]): visible while no [data-sd-pdp-primary] target is on screen and the
 *    end of the page (final platter CTA band, footer) is not near, so it never covers the sign-up field, the Squish
 *    Promise or the Not-food notice. It removes itself when Horizon's own <sticky-add-to-cart> exists, so there is never a second bar.
 * 4. Date gates (BUILD-SPEC 5 / 4): [data-sd-pdp-phase="xmas risk late normal"] and [data-sd-pdp-offer] are
 *    re-applied in New York time (Liquid printed the first paint; cached pages are corrected here).
 *    QA: ?sd_date=2026-11-24 (read by sd-ui's nyToday).
 * 5. Squish loops ([data-sd-pdp-loop]) play muted only while in view; under reduced motion they stay on the
 *    poster with controls.
 * 6. Gallery photo-truth filter and real-photo group: media ids on [data-sd-hide-media] (snippets/sd-pdp-gallery-filter.liquid) are
 *    already hidden by CSS; here their phone slides get [hidden] (Horizon's slideshow skips those), the spare dots
 *    are hidden and re-pointed at the remaining slides, and the zoom dialog thumbnails follow. Re-run after
 *    Horizon re-renders the gallery for a new variant.
 */
import { StandardEvents } from '@shopify/events';
import { morph } from '@theme/morph';
import { nyToday, renderDelivery, initReveal } from '@theme/sd-ui';

export const PDP_DATES = Object.freeze({
  xmasBy: '2026-11-22', // "Christmas: order by Sun Nov 22 · estimated arrival by Dec 17"
  riskTo: '2026-11-24', // "Ordering now may not arrive before Dec 25"
  lateTo: '2026-12-24', // "Orders now will most likely arrive after Christmas ..."
  offerTo: '2026-10-19', // Opening offer ended early: the Mini was dropped (2026-10-20)
});

/** @returns {{phase: 'xmas'|'risk'|'late'|'normal', offer: boolean, today: string}} */
export function pdpPhase(today = nyToday().toISOString().slice(0, 10)) {
  /** @type {'xmas'|'risk'|'late'|'normal'} */
  let phase = 'normal';
  if (today <= PDP_DATES.xmasBy) phase = 'xmas';
  else if (today <= PDP_DATES.riskTo) phase = 'risk';
  else if (today <= PDP_DATES.lateTo) phase = 'late';
  return { phase, offer: today <= PDP_DATES.offerTo, today };
}

export function applyGates(root = document) {
  const { phase, offer } = pdpPhase();
  for (const el of root.querySelectorAll('[data-sd-pdp-phase]')) {
    const wanted = (el.getAttribute('data-sd-pdp-phase') || '').split(/\s+/);
    /** @type {HTMLElement} */ (el).hidden = !wanted.includes(phase);
  }
  for (const el of root.querySelectorAll('[data-sd-pdp-offer]')) {
    /** @type {HTMLElement} */ (el).hidden = !offer;
  }
}

/* ------------------------------------------------------------------ */
/* 2. Secondary label on Horizon's add-to-cart                          */
/* ------------------------------------------------------------------ */

function buyLabel() {
  const src = document.querySelector('[data-sd-pdp-buy-label]');
  return src instanceof HTMLElement ? src.dataset.sdPdpBuyLabel || '' : '';
}

function relabel() {
  const label = buyLabel();
  if (!label) return;
  for (const button of document.querySelectorAll('.product-details product-form-component button[ref="addToCartButton"]')) {
    if (!(button instanceof HTMLButtonElement) || button.disabled) continue;
    const textNode = [...button.childNodes].find((n) => n.nodeType === Node.TEXT_NODE && n.textContent?.trim());
    if (textNode && textNode.textContent?.trim() !== label) textNode.textContent = label;
    button.classList.add('sd-pdp-buy');
  }
}

function watchBuyButtons() {
  const forms = document.querySelectorAll('.product-details product-form-component');
  if (!forms.length) return;
  let queued = false;
  const observer = new MutationObserver(() => {
    if (queued) return;
    queued = true;
    queueMicrotask(() => {
      queued = false;
      relabel();
    });
  });
  for (const form of forms) observer.observe(form, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['disabled'] });
  relabel();
}

/* ------------------------------------------------------------------ */
/* 1. Variant sync                                                      */
/* ------------------------------------------------------------------ */

/** @param {Document} html */
function syncFrom(html) {
  for (const el of document.querySelectorAll('[data-sd-sync]')) {
    const key = el.getAttribute('data-sd-sync');
    if (!key) continue;
    const fresh = html.querySelector(`[data-sd-sync="${CSS.escape(key)}"]`);
    if (fresh) morph(el, fresh);
  }
  renderDelivery(document);
  applyGates(document);
  relabel();
}

function onProductSelect(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;
  if (target.closest('product-card, .sd-card, quick-add-dialog, dialog')) return;
  if (!target.closest('.product-information, .shopify-section')) return;
  const promise = event.promise;
  if (!promise || typeof promise.then !== 'function') return;
  promise
    .then((result) => {
      const html = result?.detail?.html;
      if (html instanceof Document) syncFrom(html);
    })
    .catch(() => {
      /* aborted by a newer selection: the newer event syncs */
    });
}

/* ------------------------------------------------------------------ */
/* 3. Mobile sticky bar                                                 */
/* ------------------------------------------------------------------ */

function initSticky() {
  const bar = document.querySelector('[data-sd-pdp-sticky]');
  if (!(bar instanceof HTMLElement)) return;
  if (document.querySelector('sticky-add-to-cart')) {
    bar.remove();
    return;
  }

  // Two kinds of targets: a primary CTA on screen means the bar is not needed; the end of the page (the final
  // platter CTA band and the footer, with its sign-up field, Squish Promise and Not-food notice) means the bar
  // must step aside so it never covers them. The end targets count from 120px below the viewport, so the bar
  // is already gone when they scroll in.
  const primary = new Map();
  const ending = new Map();
  let visible = false;
  const setVisible = (next) => {
    if (next === visible) return;
    visible = next;
    bar.dataset.visible = String(next);
    bar.inert = !next;
    document.documentElement.classList.toggle('sd-pdp-sticky-on', next);
  };
  const update = () => {
    for (const map of [primary, ending]) for (const target of map.keys()) if (!target.isConnected) map.delete(target);
    const busy = [...primary.values()].some(Boolean) || [...ending.values()].some(Boolean);
    setVisible(!busy);
  };

  /** @param {Map<Element, boolean>} map @param {string} rootMargin */
  const watcher = (map, rootMargin) =>
    new IntersectionObserver((entries) => {
      for (const entry of entries) map.set(entry.target, entry.isIntersecting);
      update();
    }, { rootMargin });
  const primaryObserver = watcher(primary, '0px 0px -40px 0px');
  const endObserver = watcher(ending, '0px 0px 120px 0px');

  const observeTargets = () => {
    for (const target of document.querySelectorAll('[data-sd-pdp-primary], .product-details product-form-component')) {
      if (!primary.has(target)) {
        primary.set(target, false);
        primaryObserver.observe(target);
      }
    }
    for (const target of document.querySelectorAll('.sd-pdp-faq__cta, body > footer, footer:has(.sd-footer), .sd-footer')) {
      if (!ending.has(target)) {
        ending.set(target, false);
        endObserver.observe(target);
      }
    }
  };
  observeTargets();
  // Re-collect after variant morphs replace a target
  document.addEventListener('sd:pdp-synced', observeTargets);
}

/* ------------------------------------------------------------------ */
/* 5. Squish loops                                                      */
/* ------------------------------------------------------------------ */

function initLoops() {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  /** @type {Set<HTMLVideoElement>} */
  const videos = new Set();

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const video = /** @type {HTMLVideoElement} */ (entry.target);
        if (entry.isIntersecting && !reduce.matches && !document.hidden) {
          video.muted = true;
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      }
    },
    { threshold: 0.35 }
  );

  const setMotion = (video) => {
    video.muted = true;
    video.controls = reduce.matches;
    if (reduce.matches) video.pause();
  };

  // The profile loop and the gallery loop (snippets/product-media.liquid); the gallery can be morphed on a
  // variant change, so new loop elements are picked up as they appear.
  const scan = () => {
    for (const video of videos) {
      if (!video.isConnected) {
        io.unobserve(video);
        videos.delete(video);
      }
    }
    for (const video of document.querySelectorAll('video[data-sd-pdp-loop]')) {
      if (!(video instanceof HTMLVideoElement) || videos.has(video)) continue;
      videos.add(video);
      setMotion(video);
      io.observe(video);
    }
  };
  scan();

  reduce.addEventListener?.('change', () => {
    for (const video of videos) setMotion(video);
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) for (const video of videos) video.pause();
  });
  let frame = 0;
  new MutationObserver(() => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(scan);
  }).observe(document.body, { childList: true, subtree: true });
}

/* ------------------------------------------------------------------ */
/* 6. Gallery photo-truth filter                                        */
/* ------------------------------------------------------------------ */

function hiddenMediaIds() {
  const ids = new Set();
  for (const el of document.querySelectorAll('[data-sd-hide-media]')) {
    for (const id of (el.getAttribute('data-sd-hide-media') || '').split(/\s+/)) if (id) ids.add(id);
  }
  return ids;
}

/** @param {Element} el @param {Set<string>} ids */
const holdsHidden = (el, ids) =>
  [...el.querySelectorAll('[data-media-id]')].some((m) => ids.has(m.getAttribute('data-media-id') || ''));

/** Real photos shown in the gallery: media id -> aspect of the picture area (Liquid, gallery filter snippet) */
function realMedia() {
  const map = new Map();
  for (const el of document.querySelectorAll('[data-sd-real-media]')) {
    for (const pair of (el.getAttribute('data-sd-real-media') || '').split(/\s+/)) {
      const [id, ar] = pair.split(':');
      if (id) map.set(id, Number(ar) || 1);
    }
  }
  return map;
}

/** @param {Element} cell */
const mediaIdOf = (cell) => cell.querySelector('[data-media-id]')?.getAttribute('data-media-id') || '';

/**
 * Desktop grid order and spans (two columns, first image full width). Renders keep their order, then the
 * "Real sample photos" caption card, then square real photos, then wide (group) real photos as full-width 3:2
 * tiles. The caption is a square tile or a full-width strip, whichever keeps the half-width tiles in pairs; when
 * a render would still sit alone in its row it becomes a full-width 3:2 tile, so no row ever holds an orphan.
 * Only CSS `order` and data attributes change: Horizon's zoom indexes stay valid.
 * @param {Element} gallery
 */
function layoutGrid(gallery) {
  const grid = gallery.querySelector('.media-gallery__grid');
  if (!(grid instanceof HTMLElement)) return;
  const real = realMedia();
  const tpl = document.querySelector('template[data-sd-realcap-tpl]');

  let cap = grid.querySelector(':scope > [data-sd-realcap]');
  if (!cap && tpl instanceof HTMLTemplateElement) {
    cap = tpl.content.firstElementChild?.cloneNode(true) || null;
    if (cap instanceof Element) grid.append(cap);
  }

  const cells = [...grid.children].filter(
    (li) => li instanceof HTMLElement && li !== cap && li.matches('li') && !li.hidden && getComputedStyle(li).display !== 'none'
  );
  if (!cells.length) return;
  const [first, ...rest] = cells;
  const renders = [];
  const squares = [];
  const wides = [];
  for (const cell of rest) {
    const ar = real.get(mediaIdOf(cell));
    if (ar === undefined) renders.push(cell);
    else if (ar >= 1.3) wides.push(cell);
    else squares.push(cell);
  }

  /** @type {Map<Element, string>} */
  const spans = new Map();
  let capMode = '';
  if (cap) {
    const nr = renders.length;
    const nq = squares.length;
    if ((nr + nq) % 2 === 1) capMode = 'half';
    else if (nr % 2 === 0) capMode = 'banner';
    else {
      // Both odd: the last render goes full width (a stage render survives a 3:2 crop; a hand-held phone
      // shot does not), and the caption pairs with the first real photo
      capMode = 'half';
      spans.set(renders[renders.length - 1], 'wide');
    }
  } else if (renders.length % 2 === 1) {
    spans.set(renders[renders.length - 1], 'wide');
  }
  for (const cell of wides) spans.set(cell, 'wide');

  const ordered = [first, ...renders, ...(cap ? [cap] : []), ...squares, ...wides];
  ordered.forEach((cell, i) => {
    if (!(cell instanceof HTMLElement)) return;
    const order = String(i);
    if (cell.style.order !== order) cell.style.order = order;
    const span = cell === cap ? capMode : spans.get(cell) || '';
    if ((cell.getAttribute('data-sd-span') || '') !== span) {
      if (span) cell.setAttribute('data-sd-span', span);
      else cell.removeAttribute('data-sd-span');
    }
  });
  // Cells that are hidden now (variant change) drop any span they had
  for (const li of grid.children) {
    if (li instanceof HTMLElement && !ordered.includes(li) && li.hasAttribute('data-sd-span')) li.removeAttribute('data-sd-span');
  }
}

function filterGallery() {
  for (const gallery of document.querySelectorAll('.product-information__media media-gallery')) layoutGrid(gallery);
  const ids = hiddenMediaIds();
  if (!ids.size) return;
  for (const gallery of document.querySelectorAll('.product-information__media media-gallery')) {
    // Phone slideshow: hide the slides, then keep one dot per remaining slide, each tied to that slide's timeline
    const slides = [...gallery.querySelectorAll('slideshow-slide')];
    const visible = [];
    for (const slide of slides) {
      if (holdsHidden(slide, ids)) {
        if (!slide.hidden) slide.hidden = true;
      } else if (!slide.hidden) {
        visible.push(slide);
      }
    }
    for (const list of gallery.querySelectorAll('.slideshow-controls__dots')) {
      const dots = [...list.querySelectorAll('button')];
      dots.forEach((dot, i) => {
        const item = dot.closest('li') || dot;
        const slide = visible[i];
        if (!slide) {
          item.hidden = true;
          return;
        }
        item.hidden = false;
        const timeline = slide.style.getPropertyValue('--slideshow-timeline').trim();
        if (timeline && dot.style.animationTimeline !== timeline) dot.style.animationTimeline = timeline;
        const label = `Slide ${i + 1} of ${visible.length}`;
        if (dot.getAttribute('aria-label') !== label) dot.setAttribute('aria-label', label);
      });
    }
    for (const counter of gallery.querySelectorAll('.slideshow-controls__counter')) {
      const total = counter.lastChild;
      if (total && total.nodeType === Node.TEXT_NODE && total.textContent?.trim() !== String(visible.length)) {
        total.textContent = String(visible.length);
      }
    }
    // Grid cells (CSS already hides them; [hidden] keeps assistive tech in step)
    for (const cell of gallery.querySelectorAll('.media-gallery__grid > li')) {
      if (holdsHidden(cell, ids) && !cell.hidden) cell.hidden = true;
    }
    // Zoom dialog: same order as its thumbnails
    const zoomed = [...gallery.querySelectorAll('.dialog-zoomed-gallery > li')];
    const thumbs = [...gallery.querySelectorAll('.dialog-thumbnails-list__thumbnail')];
    zoomed.forEach((item, i) => {
      const match = /^product-(\d+)-/.exec(item.id);
      if (match && ids.has(match[1])) {
        if (!item.hidden) item.hidden = true;
        if (thumbs[i] && !thumbs[i].hidden) thumbs[i].hidden = true;
      }
    });
  }
}

function watchGallery() {
  filterGallery();
  const root = document.querySelector('.product-information__media');
  if (!root) return;
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      filterGallery();
    });
  }).observe(root, { childList: true, subtree: true });
  document.addEventListener('sd:pdp-synced', () => requestAnimationFrame(filterGallery));
}

/* ------------------------------------------------------------------ */

function init() {
  if (document.documentElement.dataset.sdPdp === 'ready') return;
  document.documentElement.dataset.sdPdp = 'ready';
  applyGates(document);
  watchBuyButtons();
  initSticky();
  initLoops();
  watchGallery();
  document.addEventListener(StandardEvents.productSelect, (event) => {
    onProductSelect(event);
    const promise = /** @type {any} */ (event).promise;
    promise?.then?.(() => document.dispatchEvent(new CustomEvent('sd:pdp-synced'))).catch?.(() => {});
  });
  initReveal(document);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
